import { ExternalLink, Hand, Keyboard, Monitor, RotateCw } from 'lucide-react';
import type { ReactElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';

import type { PermissionName, PermissionStatus } from '@heidi/agents/contracts';
import { relaunchApp } from '../api/permission-bridge';
import { permissionDescription, permissionLabel } from '../lib/permission-labels';

// Canonical glyph per macOS permission, matching the Settings → Permissions
// page (Hand for accessibility, not the person glyph). Kept local: the gate
// (rpa feature) shouldn't depend on the settings feature for three icons.
const PERMISSION_ICONS: Record<PermissionName, ReactElement> = {
  screen_recording: <Monitor />,
  accessibility: <Hand />,
  input_monitoring: <Keyboard />,
};

interface PermissionRowProps {
  permission: PermissionName;
  state: PermissionStatus;
  onRequest: () => void;
}

export function PermissionRow({ permission, state, onRequest }: PermissionRowProps) {
  const intl = useIntl();
  const icon = PERMISSION_ICONS[permission];

  return (
    <div
      className="flex items-start justify-between gap-4"
      data-testid={`desktop-permission-row-${permission}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <PermissionRowIcon state={state} icon={icon} />
        <div className="flex min-w-0 flex-col gap-1">
          <TypographyP2Bold>{permissionLabel(intl, permission)}</TypographyP2Bold>
          <TypographyCaption className="text-text-secondary">
            {permissionDescription(intl, permission)}
          </TypographyCaption>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <PermissionRowAction
          permission={permission}
          state={state}
          onRequest={onRequest}
        />
      </div>
    </div>
  );
}

// Literal `state` per branch: `FramedIcon`'s props are a discriminated union,
// so a computed value wouldn't narrow to an arm.
function PermissionRowIcon({
  state,
  icon,
}: {
  state: PermissionStatus;
  icon: ReactElement;
}) {
  if (state === 'granted') return <FramedIcon state="success" icon={icon} size="md" />;
  if (state === 'needs_relaunch') {
    return <FramedIcon state="warning" icon={icon} size="md" />;
  }
  return <FramedIcon state="neutral" icon={icon} size="md" />;
}

function PermissionRowAction({ permission, state, onRequest }: PermissionRowProps) {
  switch (state) {
    case 'granted':
      return (
        <Badge color="green" size="sm">
          <FormattedMessage
            defaultMessage="Granted"
            id="Z6ZsjSfZPc"
            description="Status badge for a granted desktop permission"
          />
        </Badge>
      );
    case 'needs_relaunch':
      return (
        <>
          <Badge color="yellow" size="sm">
            <FormattedMessage
              defaultMessage="Restart to finish"
              id="GXZ4WWu5gm"
              description="Status badge for a desktop permission that is granted but needs Heidi to restart before it takes effect"
            />
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void relaunchApp()}
            data-testid={`desktop-permission-relaunch-${permission}`}
          >
            <RotateCw className="size-4" />
            <FormattedMessage
              defaultMessage="Quit & Reopen"
              id="16V8Hz+Zt3"
              description="Button that quits and reopens Heidi so a newly granted desktop permission takes effect"
            />
          </Button>
        </>
      );
    case 'denied':
    case 'unknown':
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={onRequest}
          data-testid={`desktop-permission-request-${permission}`}
        >
          <ExternalLink className="size-4" />
          <FormattedMessage
            defaultMessage="Open settings"
            id="sZIXTXRdDm"
            description="Button to open macOS System Settings for a desktop permission"
          />
        </Button>
      );
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}
